import React, { useRef, useId, useEffect } from "react";
import { animate } from "framer-motion";
import { cn } from "../../lib/utils";

const MASK_IMAGE =
  "https://framerusercontent.com/images/ceBGguIpUU8luwByxuQz79t7To.png";
const NOISE_IMAGE =
  "https://framerusercontent.com/images/g0QcWrxr87K0ufOxIUFBakwYA8.png";

function mapRange(value, fromLow, fromHigh, toLow, toHigh) {
  if (fromLow === fromHigh) return toLow;
  const p = (value - fromLow) / (fromHigh - fromLow);
  return toLow + p * (toHigh - toLow);
}

/**
 * Soft masked color blob + optional turbulence displacement (legal page backdrop).
 */
export function EtherealShadow({
  sizing = "fill",
  color = "rgba(128, 128, 128, 1)",
  animation = { scale: 85, speed: 85 },
  noise,
  style,
  className,
}) {
  const rawId = useId().replace(/:/g, "");
  const filterId = `ethereal-${rawId}`;
  const turbulenceRef = useRef(null);
  const animRef = useRef(null);

  const animationEnabled = Boolean(animation && animation.scale > 0);
  const displacementScale = animationEnabled
    ? Math.round(mapRange(animation.scale, 1, 100, 24, 96))
    : 0;
  const edgePad = animationEnabled ? Math.min(displacementScale + 28, 160) : 32;
  const loopSeconds = animation
    ? mapRange(animation.speed, 1, 100, 36, 10)
    : 20;

  useEffect(() => {
    animRef.current?.stop();
    animRef.current = null;
    if (!animationEnabled || !turbulenceRef.current) return undefined;

    const el = turbulenceRef.current;
    const baseA = mapRange(animation.scale ?? 85, 0, 100, 0.006, 0.014);
    const baseB = baseA * 1.85;

    animRef.current = animate(0, Math.PI * 2, {
      repeat: Infinity,
      ease: "linear",
      duration: loopSeconds,
      onUpdate: (v) => {
        const w = Math.sin(v) * 0.004;
        el.setAttribute("baseFrequency", `${baseA + w} ${baseB + w * 1.2}`);
      },
    });

    return () => {
      animRef.current?.stop();
      animRef.current = null;
    };
  }, [
    animationEnabled,
    displacementScale,
    loopSeconds,
    animation?.scale,
  ]);

  const maskCover = sizing === "stretch" ? "100% 100%" : "cover";

  return (
    <div
      className={cn("overflow-hidden bg-[#07060f]", className)}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: "100%",
        ...style,
      }}
    >
      {/* Deep base */}
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-br from-[#0a0820] via-[#0f1128] to-[#070510]"
      />

      <div
        aria-hidden
        className="absolute"
        style={{
          inset: -edgePad,
          filter:
            animationEnabled && displacementScale > 0
              ? `url(#${filterId}) blur(4px)`
              : "blur(4px)",
        }}
      >
        {animationEnabled && displacementScale > 0 ? (
          <svg
            className="pointer-events-none absolute h-0 w-0 opacity-0"
            aria-hidden
          >
            <defs>
              <filter
                id={filterId}
                x="-55%"
                y="-55%"
                width="210%"
                height="210%"
                colorInterpolationFilters="sRGB"
              >
                <feTurbulence
                  ref={turbulenceRef}
                  result="waves"
                  type="fractalNoise"
                  numOctaves="2"
                  baseFrequency="0.01 0.018"
                  seed="14"
                />
                <feDisplacementMap
                  in="SourceGraphic"
                  in2="waves"
                  scale={displacementScale}
                  xChannelSelector="R"
                  yChannelSelector="G"
                />
              </filter>
            </defs>
          </svg>
        ) : null}
        <div
          style={{
            width: "100%",
            height: "100%",
            backgroundColor: color,
            WebkitMaskImage: `url('${MASK_IMAGE}')`,
            maskImage: `url('${MASK_IMAGE}')`,
            WebkitMaskSize: maskCover,
            maskSize: maskCover,
            WebkitMaskRepeat: "no-repeat",
            maskRepeat: "no-repeat",
            WebkitMaskPosition: "center",
            maskPosition: "center",
          }}
        />
      </div>

      {noise && noise.opacity > 0 ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage: `url("${NOISE_IMAGE}")`,
            backgroundSize: `${(noise.scale ?? 1) * 200}px`,
            backgroundRepeat: "repeat",
            opacity: noise.opacity / 2,
          }}
        />
      ) : null}
    </div>
  );
}

EtherealShadow.displayName = "EtherealShadow";

/** Fixed full-viewport backdrop for Privacy / Terms pages. */
export function LegalEtherealBackdrop({ className }) {
  return (
    <div
      aria-hidden
      className={cn("pointer-events-none fixed inset-0 z-0", className)}
    >
      <EtherealShadow
        className="h-full min-h-screen w-full"
        sizing="fill"
        color="rgba(239, 68, 68, 0.22)"
        animation={{ scale: 88, speed: 82 }}
        noise={{ opacity: 1, scale: 1.1 }}
      />
    </div>
  );
}

export default EtherealShadow;
